import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DEMO_PUBKEYS, 
  DEMO_BADGES, 
  DEMO_AWARDS, 
  getDemoProfile, 
  getDemoBadge,
  getDemoUserBadges,
  DEMO_COMMUNITY,
  RANK_NAMES 
} from '@/lib/demoData';
import { validateAuthorityChain } from '@/lib/badgeUtils';
import type { BadgeReference, AuthorityChain } from '@/types/badges';

export default function UserBadgesPage() {
  const { pubkey } = useParams<{ pubkey: string }>();
  
  if (!pubkey) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>No user specified</p>
      </div>
    );
  }

  const profile = getDemoProfile(pubkey);
  const userBadges = getDemoUserBadges(pubkey);
  const isFounder = [DEMO_PUBKEYS.alice, DEMO_PUBKEYS.bob].includes(pubkey);
  
  // Build badge refs for chain validation
  const badgeRefs = DEMO_BADGES.map(b => ({
    kind: 30009,
    pubkey: b.pubkey,
    identifier: b.identifier,
    relay: undefined,
    rank: b.rank,
  }));

  const communityMeta = {
    id: DEMO_COMMUNITY.id,
    name: DEMO_COMMUNITY.name,
    badges: badgeRefs,
    founders: [DEMO_PUBKEYS.alice, DEMO_PUBKEYS.bob],
    relays: ['wss://relay.example.com'],
  };

  const awards = DEMO_AWARDS.map(a => ({
    kind: 8 as const,
    pubkey: a.granter,
    tags: [
      ['a', `30009:${DEMO_PUBKEYS.alice}:${a.badgeIdentifier}`],
      ['p', a.recipient],
    ],
    content: '',
    id: `demo-${a.granter}-${a.recipient}`,
    created_at: a.timestamp,
    sig: 'demo',
  }));

  // Get all authority chains for this user
  const chains: AuthorityChain[] = [];
  for (const award of awards) {
    const awardData = award.tags.find(t => t[0] === 'p');
    if (awardData?.[1] === pubkey) {
      const chain = validateAuthorityChain(award, communityMeta, awards, new Set());
      if (chain) {
        chains.push(chain);
      }
    }
  }

  // Get awards made by this user
  const awardsMade = DEMO_AWARDS.filter(a => a.granter === pubkey);
  const awardsReceived = DEMO_AWARDS.filter(a => a.recipient === pubkey);

  // Get highest rank
  let highestRank: number | undefined;
  for (const { badge } of userBadges) {
    if (highestRank === undefined || badge.rank < highestRank) {
      highestRank = badge.rank;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-violet-900 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500 rounded-full blur-3xl transform -translate-y-1/2" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl transform translate-y-1/2" />
        </div>

        <div className="relative z-10 px-6 py-12 md:px-12 max-w-4xl mx-auto">
          <Link to="/badges" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <BackIcon className="w-4 h-4" />
            Back to Communities
          </Link>

          <div className="flex items-start gap-6">
            <Avatar className="h-24 w-24 border-4 border-white/20">
              <AvatarImage src={profile.picture} />
              <AvatarFallback className="text-2xl">{pubkey.slice(0, 4)}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-white">{profile.name}</h1>
                {isFounder && (
                  <Badge className="bg-amber-400 text-amber-950 border-0">
                    Founder
                  </Badge>
                )}
              </div>
              <p className="text-white/80 font-mono text-sm mb-3">{pubkey}</p>
              <p className="text-white/70 max-w-xl">{profile.about}</p>

              <div className="flex gap-6 mt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{userBadges.length}</p>
                  <p className="text-xs text-white/60">Badges</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{awardsMade.length}</p>
                  <p className="text-xs text-white/60">Awarded</p>
                </div>
                {highestRank !== undefined && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{highestRank}</p>
                    <p className="text-xs text-white/60">Rank</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Tabs defaultValue="badges" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="chains">Authority Chains</TabsTrigger>
            <TabsTrigger value="granting">Granting</TabsTrigger>
          </TabsList>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-6">
            {userBadges.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userBadges.map(({ badge }, i) => {
                  const badgeInfo = getDemoBadge(badge.identifier);
                  const chain = chains.find(c => c.badge.identifier === badge.identifier);
                  
                  return (
                    <Card key={i} className={chain && !chain.isValid ? 'opacity-60 border-dashed' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <img
                            src={badgeInfo?.thumb || badgeInfo?.image}
                            alt={badgeInfo?.name}
                            className="w-16 h-16 rounded-full border-2 object-cover"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{badgeInfo?.name}</h3>
                              <Badge variant="outline" className="text-xs">
                                Rank {badge.rank}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {badgeInfo?.description}
                            </p>
                            {chain && (
                              <div className="mt-2">
                                {!chain.isValid && (
                                  <Badge variant="destructive" className="text-xs">
                                    Invalid: {chain.invalidReason}
                                  </Badge>
                                )}
                                {chain.isValid && (
                                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs">
                                    Valid Chain
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <BadgeIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No Badges Yet</h3>
                  <p className="text-sm text-muted-foreground">
                    This user hasn't received any badges in this community.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Chains Tab */}
          <TabsContent value="chains" className="space-y-4">
            {chains.length > 0 ? (
              chains.map((chain, i) => (
                <Card key={i} className={!chain.isValid ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">
                        Chain to {chain.badge.identifier.replace(/-/g, ' ')}
                      </CardTitle>
                      {!chain.isValid && (
                        <Badge variant="destructive" className="text-xs">
                          Invalid
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {buildChainPath(chain).map((node, j) => {
                        const nodeProfile = getDemoProfile(node.pubkey);
                        return (
                          <div key={j} className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={nodeProfile.picture} />
                                <AvatarFallback className="text-xs">
                                  {node.pubkey.slice(0, 4)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm">
                                {nodeProfile.name}
                              </span>
                              {j < buildChainPath(chain).length - 1 && (
                                <span className="text-muted-foreground">→</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <ChainIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No Authority Chains</h3>
                  <p className="text-sm text-muted-foreground">
                    Authority chains will appear here once this user receives badges.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Granting Tab */}
          <TabsContent value="granting" className="space-y-4">
            {awardsMade.length > 0 ? (
              <>
                <h3 className="font-semibold">Badges Awarded by This User</h3>
                <div className="space-y-2">
                  {awardsMade.map((award, i) => {
                    const recipientProfile = getDemoProfile(award.recipient);
                    const badgeInfo = getDemoBadge(award.badgeIdentifier);
                    const badge = DEMO_BADGES.find(b => b.identifier === award.badgeIdentifier);
                    
                    return (
                      <Card key={i}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={recipientProfile.picture} />
                              <AvatarFallback>
                                {award.recipient.slice(0, 4)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium">{recipientProfile.name}</p>
                              <p className="text-sm text-muted-foreground">
                                Received: {badgeInfo?.name}
                              </p>
                            </div>
                            {badge && (
                              <img
                                src={badge.thumb || badge.image}
                                alt={badge.name}
                                className="w-10 h-10 rounded-full border"
                              />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <AwardIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">No Badges Awarded</h3>
                  <p className="text-sm text-muted-foreground">
                    {isFounder 
                      ? 'As a founder, you can award any badge to members.'
                      : 'This user has not yet awarded any badges.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <footer className="border-t bg-card py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>
            Built with{" "}
            <a 
              href="https://shakespeare.diy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
            {" "}• Hierarchical Badges Demo (NIP-XX Draft)
          </p>
        </div>
      </footer>
    </div>
  );
}

// Helper to build chain path
function buildChainPath(chain: AuthorityChain): { pubkey: string; badge: BadgeReference }[] {
  const path: { pubkey: string; badge: BadgeReference }[] = [];
  
  let current: AuthorityChain | undefined = chain;
  while (current) {
    path.unshift({ pubkey: current.granter, badge: current.badge });
    current = current.parentChain;
  }
  
  // Add final recipient
  path.push({ pubkey: chain.recipient, badge: chain.badge });
  
  return path;
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function ChainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function AwardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  );
}
